import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import { Badge, Button, Typography, useMediaQuery } from "@mui/material";
import { Chip, Drawer, IconButton } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { BubbleChatIcon } from "@hugeicons-pro/core-stroke-rounded";
import { Inline } from "components/layout/reusableLayouts";
import { ParentType } from "enums/Comments";
import CardRequestStatus from "enums/CardRequestStatus";
import { useAdminTools } from "hooks/useAdminTools";
import { GET_CARD_DETAIL } from "graphql/card";
import { useComments } from "hooks/useComments";
import CommentsPanel, {
  DEFAULT_DRAWER_WIDTH,
} from "components/common/comments/CommentsPanel";
import IdNotFoundPage from "components/common/IdNotFoundPage";
import CardStatusChip from "components/common/cards/CardStatusChip";
import { isCardPendingTerms } from "utils/cards/cardRowData";
import CardDetails, {
  cardPolicyView,
  type CardDetailCard,
} from "components/common/cards/CardDetails";
import CardActions from "components/common/cards/CardActions";

interface CardDetailsDrawerProps {
  open: boolean;
  cardId: string | null;
  /** `didChange` tells the list behind whether it needs to refetch. */
  onClose: (didChange: boolean) => void;
  /** Fires once the drawer has slid out. */
  onAfterClose?: () => void;
}

/**
 * The card details drawer, for every route: the header, tabs, comments, actions
 * and close gate are the same card wherever it is opened from.
 */
export const CardDetailsDrawer: React.FC<CardDetailsDrawerProps> = ({
  open,
  cardId,
  onClose,
  onAfterClose,
}) => {
  const { t } = useTranslation();
  const adminTools = useAdminTools();

  const [didChange, setDidChange] = useState(false);
  const [busy, setBusy] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);

  // The panel docks beside the drawer only when there is room for both.
  const commentsDockable = useMediaQuery(
    `(min-width:${DEFAULT_DRAWER_WIDTH + 520}px)`,
  );
  const commentActions = useComments(cardId ?? "", ParentType.CARD);
  const commentCount = commentActions.comments?.length ?? 0;

  const { data, loading, refetch } = useQuery(GET_CARD_DETAIL, {
    variables: { id: cardId ?? "" },
    skip: !cardId,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (!open) {
      setDidChange(false);
      setCommentsOpen(false);
    }
  }, [open]);

  const card: CardDetailCard | undefined = data?.card;
  const { requestLabel } = cardPolicyView(card);

  const close = () => {
    if (busy) return;
    setCommentsOpen(false);
    onClose(didChange);
  };

  const refresh = () => {
    setDidChange(true);
    refetch();
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={close}
        onAfterClose={onAfterClose}
        loading={loading && !card}
        title={t("Card Details")}
        disableEnforceFocus={commentsOpen}
        headerAction={
          <Inline gap="var(--space-3)" align="center" wrap={false}>
            <CardStatusChip
              status={card?.status}
              pendingTerms={isCardPendingTerms(card)}
            />
            {requestLabel && <Chip tone="neutral">{t(requestLabel)}</Chip>}
            <IconButton
              variant="ghost"
              aria-label={t("Comments")}
              onClick={() => setCommentsOpen((wasOpen) => !wasOpen)}
            >
              <Badge badgeContent={commentCount} color="primary">
                <HugeiconsIcon icon={BubbleChatIcon} size={18} />
              </Badge>
            </IconButton>
          </Inline>
        }
        footer={
          <Button variant="default-outlined" disabled={busy} onClick={close}>
            {t("Exit")}
          </Button>
        }
      >
        {card?.id ? (
          <>
            <CardDetails
              card={card}
              loading={loading}
              actions={
                <CardActions
                  card={card}
                  open={open}
                  refresh={refresh}
                  close={() => onClose(true)}
                  setBusy={setBusy}
                />
              }
            />
            {card.pendingRequest?.requestStatus ===
              CardRequestStatus.PENDING_TERMS && (
              <Typography variant="body1" sx={{ textAlign: "center" }}>
                {t("CardIsPendingTerms")}
              </Typography>
            )}
          </>
        ) : (
          <IdNotFoundPage
            title={t("Card")}
            isLoading={loading}
            showSkeleton={true}
          />
        )}
      </Drawer>

      <CommentsPanel
        open={open && commentsOpen}
        onClose={() => setCommentsOpen(false)}
        drawerWidth={DEFAULT_DRAWER_WIDTH}
        docked={commentsDockable}
        commentActions={commentActions}
        openProfileOnMention={adminTools}
      />
    </>
  );
};

export default CardDetailsDrawer;
